/**
 * AddBudgetDialog - Dialog for adding budget assignment from investment lines
 *
 * Split into smaller step components for maintainability:
 * - UnitsStep: Select kostenplaatsen
 * - SourceStep: Select investment line (inline - simpler)
 * - DistributionStep: Distribute budget across units
 */

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useSession } from "@clerk/clerk-react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { toast } from "react-toastify";
import { CurrencyEur, Spinner, CheckCircle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ProjectPart, RenovationLabel } from "shared/types/renovation";
import type { InvestmentItem } from "screens/investment/types/investment";
import { UnitDTO } from "shared/types/unit";
import { DialogState, initialDialogState, UnitDistribution } from "./types";
import { UnitsStep, DistributionStep } from "./steps";
import { SourceStep } from "./SourceStep";

interface AddBudgetDialogProps {
  part: ProjectPart;
  buildingId: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const AddBudgetDialog: React.FC<AddBudgetDialogProps> = ({
  part,
  buildingId,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { session } = useSession();
  const [dialogState, setDialogState] = useState<DialogState>(initialDialogState);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Data
  const [buildingUnits, setBuildingUnits] = useState<UnitDTO[]>([]);
  const [allInvestmentItems, setAllInvestmentItems] = useState<InvestmentItem[]>([]);
  const [labels, setLabels] = useState<RenovationLabel[]>([]);
  const [loadingUnits, setLoadingUnits] = useState(false);
  const [loadingItems, setLoadingItems] = useState(false);

  // Search
  const [unitSearch, setUnitSearch] = useState("");
  const [budgetSearch, setBudgetSearch] = useState("");

  // Reset on open
  useEffect(() => {
    if (isOpen) {
      const projectUnitIds =
        (part.project as any)?.units?.map((u: { id: string }) => u.id).filter(Boolean) as
          | string[]
          | undefined || [];
      setDialogState({
        ...initialDialogState,
        isOpen: true,
        unitIds: projectUnitIds,
        labelFilter: part.label?.id || null,
      });
      setUnitSearch("");
      setBudgetSearch("");
    } else {
      setDialogState(initialDialogState);
    }
  }, [isOpen, part]);

  // Fetch labels
  useEffect(() => {
    if (!session) return;
    fetchWithAuth(`${API_URL}/renovation/labels`, { method: "GET" }, session)
      .then((res) => res.json())
      .then((data) => setLabels(Array.isArray(data) ? data : []))
      .catch((err) => console.error("Error loading labels:", err));
  }, [session]);

  // Fetch units
  useEffect(() => {
    if (!isOpen || !buildingId || !session) return;
    setLoadingUnits(true);
    fetchWithAuth(`${API_URL}/units/building/${buildingId}`, { method: "GET" }, session)
      .then((res) => res.json())
      .then((data) => setBuildingUnits(Array.isArray(data) ? data : data.data || []))
      .catch((err) => console.error("Error loading units:", err))
      .finally(() => setLoadingUnits(false));
  }, [isOpen, buildingId, session]);

  // Fetch investment items
  useEffect(() => {
    if (!isOpen || !buildingId || !session) return;
    setLoadingItems(true);
    fetchWithAuth(
      `${API_URL}/investment/items?buildingId=${buildingId}&excludeConverted=false`,
      { method: "GET" },
      session
    )
      .then((res) => res.json())
      .then((data) => setAllInvestmentItems(data.data || data || []))
      .catch((err) => console.error("Error loading investment items:", err))
      .finally(() => setLoadingItems(false));
  }, [isOpen, buildingId, session]);

  // Get matching items for units - show ALL items including fully allocated ones
  const getMatchingItemsForUnits = useCallback(
    (unitIds: string[], labelFilterId: number | null) => {
      return allInvestmentItems.filter((item) => {
        if (!item.units?.some((u) => unitIds.includes(u.id!))) return false;
        if (labelFilterId !== null && item.label?.id !== labelFilterId) return false;
        // Show all matching items, including 100% allocated ones
        return true;
      });
    },
    [allInvestmentItems]
  );

  // Get item by ID
  const getItemById = useCallback(
    (itemId: number) => allInvestmentItems.find((i) => i.id === itemId),
    [allInvestmentItems]
  );

  // Distribution helpers
  const initializeEqualDistribution = useCallback(() => {
    const equalPercentage = 100 / dialogState.unitIds.length;
    const distribution: UnitDistribution = {};
    let runningTotal = 0;

    dialogState.unitIds.forEach((unitId, index) => {
      if (index === dialogState.unitIds.length - 1) {
        // Last unit gets the remainder to ensure exactly 100%
        distribution[unitId] = Math.round((100 - runningTotal) * 100) / 100;
      } else {
        const rounded = Math.round(equalPercentage * 100) / 100;
        distribution[unitId] = rounded;
        runningTotal += rounded;
      }
    });
    return distribution;
  }, [dialogState.unitIds]);

  const totalDistributionPercentage = useMemo(() => {
    return Object.values(dialogState.unitDistribution).reduce((sum, pct) => sum + (pct || 0), 0);
  }, [dialogState.unitDistribution]);

  const isDistributionValid = useMemo(() => {
    return Math.abs(totalDistributionPercentage - 100) < 0.01;
  }, [totalDistributionPercentage]);

  // Check if all required reasons are provided for audit trail
  const isReasonValid = useMemo(() => {
    // For new budget: need creationReason
    if (dialogState.isNewBudget) {
      return dialogState.creationReason.trim().length >= 5;
    }

    // For existing item with overflow
    if (dialogState.selectedItemId) {
      const item = getItemById(dialogState.selectedItemId);
      if (item) {
        const remaining = Number(item.estimatedCostExclVat) - Number(item.allocatedBudget || 0);
        const overflowAmount = dialogState.amount - remaining;

        if (overflowAmount > 0) {
          // Overflow with budget increase: need overflowReason
          if (dialogState.overflowOption === "increase") {
            return dialogState.overflowReason.trim().length >= 5;
          }
          // Overflow with new line: need overflowCreationReason
          if (dialogState.overflowOption === "new_line") {
            return dialogState.overflowCreationReason.trim().length >= 5;
          }
          // No overflow option selected yet
          return false;
        }
      }
    }

    return true;
  }, [
    dialogState.isNewBudget,
    dialogState.creationReason,
    dialogState.selectedItemId,
    dialogState.amount,
    dialogState.overflowOption,
    dialogState.overflowReason,
    dialogState.overflowCreationReason,
    getItemById,
  ]);

  // Handlers
  const handleUnitsConfirm = () => {
    if (dialogState.unitIds.length === 0) return;
    const matching = getMatchingItemsForUnits(dialogState.unitIds, dialogState.labelFilter);
    setBudgetSearch("");
    setDialogState((prev) => ({ ...prev, matchingItems: matching, step: "source" }));
  };

  const handleProceedToDistribution = () => {
    const initialDistribution = initializeEqualDistribution();
    setDialogState((prev) => ({
      ...prev,
      step: "distribution",
      unitDistribution: initialDistribution,
    }));
  };

  // Submit
  const handleSubmit = async () => {
    if (!session || !part.id) return;

    if (dialogState.amount <= 0) {
      toast.error("Voer een bedrag in");
      return;
    }

    if (dialogState.isNewBudget && !dialogState.newBudgetLabelId) {
      toast.error("Selecteer een categorie voor de nieuwe investeringslijn");
      return;
    }

    // Validate creation reason for new investment line (audit trail)
    if (
      dialogState.isNewBudget &&
      (!dialogState.creationReason.trim() || dialogState.creationReason.trim().length < 5)
    ) {
      toast.error("Geef een reden voor het aanmaken van de investeringslijn (minimaal 5 karakters)");
      return;
    }

    if (dialogState.selectedItemId && !dialogState.isNewBudget) {
      const item = getItemById(dialogState.selectedItemId);
      if (item) {
        const remaining = Number(item.estimatedCostExclVat) - Number(item.allocatedBudget || 0);
        const overflowAmount = dialogState.amount - remaining;

        if (overflowAmount > 0) {
          if (!dialogState.overflowOption) {
            toast.error("Kies hoe het extra budget moet worden behandeld");
            return;
          }
          if (
            dialogState.overflowOption === "increase" &&
            (!dialogState.overflowReason.trim() || dialogState.overflowReason.trim().length < 5)
          ) {
            toast.error("Geef een reden voor de budgetverhoging (minimaal 5 karakters)");
            return;
          }
          if (dialogState.overflowOption === "new_line" && !dialogState.newBudgetLabelId) {
            toast.error("Selecteer een categorie voor de nieuwe investeringslijn");
            return;
          }
          // Validate overflow creation reason for audit trail
          if (
            dialogState.overflowOption === "new_line" &&
            (!dialogState.overflowCreationReason.trim() ||
              dialogState.overflowCreationReason.trim().length < 5)
          ) {
            toast.error(
              "Geef een reden voor het aanmaken van de extra investeringslijn (minimaal 5 karakters)"
            );
            return;
          }
        }
      }
    }

    setIsSubmitting(true);

    try {
      const requestBody: any = {
        amount: dialogState.amount,
        unitIds: dialogState.unitIds,
        unitDistribution:
          Object.keys(dialogState.unitDistribution).length > 0
            ? dialogState.unitDistribution
            : undefined,
      };

      if (dialogState.isNewBudget) {
        requestBody.createNewLine = true;
        requestBody.newLineLabelId = dialogState.newBudgetLabelId;
        requestBody.creationReason = dialogState.creationReason.trim();
      } else if (dialogState.selectedItemId) {
        requestBody.investmentItemId = dialogState.selectedItemId;

        const item = getItemById(dialogState.selectedItemId);
        if (item) {
          const remaining = Number(item.estimatedCostExclVat) - Number(item.allocatedBudget || 0);
          const overflowAmount = dialogState.amount - remaining;

          if (overflowAmount > 0 && dialogState.overflowOption === "increase") {
            requestBody.budgetIncreaseAmount = overflowAmount;
            requestBody.budgetIncreaseReason = dialogState.overflowReason.trim();
          } else if (overflowAmount > 0 && dialogState.overflowOption === "new_line") {
            requestBody.amount = remaining;
            requestBody.createOverflowLine = true;
            requestBody.overflowAmount = overflowAmount;
            requestBody.overflowLabelId = dialogState.newBudgetLabelId;
            requestBody.overflowCreationReason = dialogState.overflowCreationReason.trim();
          }
        }
      }

      const response = await fetchWithAuth(
        `${API_URL}/renovation/project/part/${part.id}/investment-assignment`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestBody),
        },
        session
      );

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || "Failed to assign budget");
      }

      toast.success("Budget succesvol toegewezen");
      onSuccess();
      onClose();
    } catch (error) {
      console.error("Error assigning budget:", error);
      toast.error(error instanceof Error ? error.message : "Fout bij toewijzen budget");
    } finally {
      setIsSubmitting(false);
    }
  };

  const canSubmitFromSource =
    (dialogState.selectedItemId || dialogState.isNewBudget) && dialogState.amount > 0;

  return (
    <TooltipProvider>
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="max-h-[85vh] max-w-lg overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CurrencyEur className="h-5 w-5 text-moss-primary" />
              Budget toewijzen
            </DialogTitle>
            <DialogDescription>
              {dialogState.step === "units" && "Stap 1: Selecteer kostenplaats(en)"}
              {dialogState.step === "source" && "Stap 2: Kies investeringslijn of maak nieuw aan"}
              {dialogState.step === "distribution" && "Stap 3: Verdeel budget over kostenplaatsen"}
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto py-4 space-y-4">
            {dialogState.step === "units" && (
              <UnitsStep
                unitIds={dialogState.unitIds}
                setUnitIds={(ids) => setDialogState((prev) => ({ ...prev, unitIds: ids }))}
                buildingUnits={buildingUnits}
                loadingUnits={loadingUnits}
                unitSearch={unitSearch}
                setUnitSearch={setUnitSearch}
                onConfirm={handleUnitsConfirm}
              />
            )}

            {dialogState.step === "source" && (
              <SourceStep
                dialogState={dialogState}
                setDialogState={setDialogState}
                labels={labels}
                allInvestmentItems={allInvestmentItems}
                loadingItems={loadingItems}
                budgetSearch={budgetSearch}
                setBudgetSearch={setBudgetSearch}
                getMatchingItemsForUnits={getMatchingItemsForUnits}
                getItemById={getItemById}
              />
            )}

            {dialogState.step === "distribution" && (
              <DistributionStep
                unitIds={dialogState.unitIds}
                amount={dialogState.amount}
                unitDistribution={dialogState.unitDistribution}
                setUnitDistribution={(dist) =>
                  setDialogState((prev) => ({ ...prev, unitDistribution: dist }))
                }
                buildingUnits={buildingUnits}
                onBack={() => setDialogState((prev) => ({ ...prev, step: "source" }))}
              />
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
              Annuleren
            </Button>
            {dialogState.step === "source" &&
              canSubmitFromSource &&
              dialogState.unitIds.length > 1 && (
                <Button
                  onClick={handleProceedToDistribution}
                  disabled={
                    (dialogState.isNewBudget && !dialogState.newBudgetLabelId) ||
                    !isReasonValid
                  }
                  className="bg-moss-primary hover:bg-moss-primary/90"
                >
                  Verdeling instellen
                </Button>
              )}
            {dialogState.step === "source" &&
              canSubmitFromSource &&
              dialogState.unitIds.length === 1 && (
                <Button
                  onClick={handleSubmit}
                  disabled={
                    isSubmitting ||
                    dialogState.amount <= 0 ||
                    (dialogState.isNewBudget && !dialogState.newBudgetLabelId) ||
                    !isReasonValid
                  }
                  className="bg-moss-primary hover:bg-moss-primary/90"
                >
                  {isSubmitting ? (
                    <>
                      <Spinner className="mr-2 h-4 w-4 animate-spin" />
                      Bezig...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="mr-2 h-4 w-4" />
                      Toewijzen
                    </>
                  )}
                </Button>
              )}
            {dialogState.step === "distribution" && (
              <Button
                onClick={handleSubmit}
                disabled={isSubmitting || !isDistributionValid || !isReasonValid}
                className="bg-moss-primary hover:bg-moss-primary/90"
              >
                {isSubmitting ? (
                  <>
                    <Spinner className="mr-2 h-4 w-4 animate-spin" />
                    Bezig...
                  </>
                ) : (
                  <>
                    <CheckCircle className="mr-2 h-4 w-4" />
                    Toewijzen
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
};

export default AddBudgetDialog;
